import React, { useRef, useState } from 'react';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import Button from '../../../Components/Button/Button';
import { postApi } from '../../../Services/apiService';
import { toast } from 'react-toastify';

export const BACKGROUND_CHECK_URL = '/verification/tenant/checkr/background-check';

const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';

// Load Stripe with error handling - prevents app crash when Stripe.js fails to load
const stripePromise = stripePublishableKey
  ? loadStripe(stripePublishableKey).catch((err) => {
      console.warn('Stripe failed to load. Payment may be unavailable.', err?.message || err);
      return null;
    })
  : null;

const elementOptions = {
  style: {
    base: {
      fontSize: '14px',
      color: '#2c2c2c',
      fontFamily:
        "'Be Vietnam Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
      '::placeholder': { color: '#9aa0a6' },
    },
    invalid: { color: '#dc3545' },
  },
};

// Creates the background check payment. Without propertyId the backend uses the tenant's latest application.
// Returns { alreadyPaid, clientSecret, amount, message }.
export const startBackgroundCheckPayment = async (propertyId) => {
  const res = await postApi(`${BACKGROUND_CHECK_URL}/pay`, propertyId ? { property_id: propertyId } : {});
  const data = res?.data || {};
  if (data.already_paid) {
    return { alreadyPaid: true, message: res?.message };
  }
  if (!data.client_secret) {
    throw new Error(res?.message || 'Unable to start background check payment');
  }
  return { alreadyPaid: false, clientSecret: data.client_secret, amount: data.amount };
};

function PaymentForm({ clientSecret, amount, onPaymentSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [isPaying, setIsPaying] = useState(false);

  // Only auto-advance focus on typed input, never on a one-shot browser autofill
  // (moving focus mid-autofill leaves the card field stuck).
  const numberEdits = useRef(0);
  const expiryEdits = useRef(0);

  const handleCardNumberChange = (event) => {
    numberEdits.current = event.empty ? 0 : numberEdits.current + 1;
    if (!event.complete || numberEdits.current < 2 || !elements) return;
    const expiryElement = elements.getElement(CardExpiryElement);
    if (expiryElement) setTimeout(() => expiryElement.focus(), 50);
  };

  const handleExpiryChange = (event) => {
    expiryEdits.current = event.empty ? 0 : expiryEdits.current + 1;
    if (!event.complete || expiryEdits.current < 2 || !elements) return;
    const cvcElement = elements.getElement(CardCvcElement);
    if (cvcElement) setTimeout(() => cvcElement.focus(), 50);
  };

  const handlePay = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !clientSecret) return;

    setIsPaying(true);
    try {
      const cardNumber = elements.getElement(CardNumberElement);
      if (!cardNumber) {
        toast.error('Card element not ready. Please try again.');
        return;
      }

      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: cardNumber },
      });

      if (error) {
        toast.error(error.message || 'Payment failed. Please try again.');
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        toast.success('Payment successful. The background check link will be emailed to you shortly.');
        onPaymentSuccess?.();
      } else {
        toast.info(`Payment status: ${paymentIntent?.status || 'unknown'}`);
      }
    } catch (err) {
      toast.error(err?.message || 'Payment failed. Please try again.');
    } finally {
      setIsPaying(false);
    }
  };

  const amountText = `$${Number(amount || 0).toFixed(2)}`;

  return (
    <form onSubmit={handlePay} className="payment-card-form background-check-card-form" noValidate autoComplete="off" data-form-type="other">
      <div className="payment-summary-card">
        <div className="payment-summary-row total">
          <div className="payment-summary-label">Background Check Fee</div>
          <div className="payment-summary-total">{amountText}</div>
        </div>
      </div>

      <div className="payment-field">
        <label className="payment-label">Card Number</label>
        <div className="payment-stripe-input" data-autocomplete="off">
          <CardNumberElement options={{ ...elementOptions, disableLink: true }} onChange={handleCardNumberChange} />
        </div>
      </div>

      <div className="payment-row">
        <div className="payment-field">
          <label className="payment-label">Expiry</label>
          <div className="payment-stripe-input" data-autocomplete="off">
            <CardExpiryElement options={{ ...elementOptions, disableLink: true }} onChange={handleExpiryChange} />
          </div>
        </div>
        <div className="payment-field">
          <label className="payment-label">CVC</label>
          <div className="payment-stripe-input" data-autocomplete="off">
            <CardCvcElement options={{ ...elementOptions, disableLink: true }} />
          </div>
        </div>
      </div>

      <div className="background-check-actions">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={isPaying}>
          Cancel
        </Button>
        <Button type="submit" variant="success" disabled={isPaying || !stripe}>
          {isPaying ? 'Processing...' : `Pay ${amountText}`}
        </Button>
      </div>
    </form>
  );
}

export const BackgroundCheckPaymentForm = (props) => {
  if (!stripePromise) {
    return (
      <div className="tenant-screening-payment-error">
        Stripe is not configured. Set REACT_APP_STRIPE_PUBLISHABLE_KEY in .env
      </div>
    );
  }
  return (
    <Elements stripe={stripePromise}>
      <PaymentForm {...props} />
    </Elements>
  );
};
